import * as SPREAD_CONFIG_LIQSRC from '@components/model-structure/liquidity-sources/liquidity-sources.spread.config';
import * as GC from '@grapecity/spread-sheets';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { ActiveDetail, ActiveSelection, ErrorsView, MessageGenerator, MyDateTime } from '@interfaces/results/results-class-library';
import { BalanceSheet } from '@models/balance-sheet';
import { ResultsetItem } from '@models/resultset-item';
import { ResultsStoreService } from '@services/results/results-store.service';
import { SettingsService } from '@services/settings/settings.service';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { getFont } from '@shared/spreadsheet/spreadsheet-config';
import { errorAlreadyLogged, logError } from '../result-errors';
import { addExogenousByGroupID } from './exogenous-builder';
import { addLiquidityToSheet } from './liquidities-builder';
import { COL_WIDTH, FAIL_COLOR, resultsSpreadConfig, ROW_COUNT, ROW_HEIGHT } from './results-spread-config';

let currentRow: number;
let numPeriods: number;
let spread: GC.Spread.Sheets.Workbook;
let settingsService: SettingsService;
let resultsStore: ResultsStoreService;

let hierarchyMapping = {};
const hierarchyKey = 'Mnemonic';
const PERIOD_DATES_START_COL = 7;

// DETAIL View Workbook
// ====================
export function createDetailsForResult(
  workbook: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  result: any,
  component: any
) {
  // Injecting required elements from parent component
  settingsService = component.settingsService;
  resultsStore = component.resultsStore;
  spread = workbook;

  // Set up spreadsheet
  workSheet.suspendCalcService();
  workSheet.suspendPaint();

  // =========================
  //   General Sheet Options
  // =========================
  workSheet.setColumnCount(9 + result.PeriodDates.length);
  workSheet.setRowCount(ROW_COUNT);

  // Events
  // ===============================
  workSheet.bind(GC.Spread.Sheets.Events.TopRowChanged, function (sender, args) {
    ActiveDetail.row = args.newTopRow;
    ActiveDetail.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeRow', ActiveDetail.row, ActiveDetail.last_change);
  });

  workSheet.bind(GC.Spread.Sheets.Events.CellClick, function (e, info) {
    ActiveSelection.row = info.row;
    ActiveSelection.col = info.col;
    ActiveSelection.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSelection', ActiveSelection);
  });

  workSheet.bind(GC.Spread.Sheets.Events.CellDoubleClick, function (e, info) {
    ActiveSelection.row = info.row;
    ActiveSelection.col = info.col;
    ActiveSelection.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSelection', ActiveSelection);
  });

  // Options
  // ===============================
  setDefaultBookOptions(spread.options, resultsSpreadConfig.spreadOptions);
  setDefaultSheetOptions(workSheet.options, { ...resultsSpreadConfig.sheetOptions });

  workSheet.options.isProtected = true;
  workSheet.options.protectionOptions.allowResizeRows = true;
  workSheet.options.protectionOptions.allowResizeColumns = true;
  workSheet.options.protectionOptions.allowOutlineColumns = true;
  workSheet.options.protectionOptions.allowOutlineRows = true;
  workSheet.defaults.rowHeight = ROW_HEIGHT;
  workSheet.defaults.colWidth = COL_WIDTH;

  // Context Menu for all result's pages
  SpreadContextMenu.setSimpleCopy(spread);

  if (spread && spread.getSheetIndex('Sheet1')) {
    spread.removeSheet(spread.getSheetIndex('Sheet1'));
  }

  setupResults(workSheet, result);

  workSheet.resumePaint();
  workSheet.resumeCalcService();
}

function setupResults(workSheet: GC.Spread.Sheets.Worksheet, result: any) {
  const isSummaryView = workSheet.name().includes('-summary');
  // Setting the tab color for export by sheetName (task 3635)
  settingsService.setSheetColor(workSheet);
  let column = 2;
  currentRow = 1;

  // -- First two columns with mnemonics
  for (let i = 0; i < column; i++) {
    workSheet.setColumnWidth(i, COL_WIDTH * 1.4, GC.Spread.Sheets.SheetArea.viewport);
  }
  // -- Account names has wider columns
  workSheet.setColumnWidth(column, COL_WIDTH * 0.7, GC.Spread.Sheets.SheetArea.viewport);
  workSheet.setColumnWidth(column + 1, COL_WIDTH * 0.25, GC.Spread.Sheets.SheetArea.viewport);

  // -- to have an indented effect on Input properties and StartBal-EndBal properties
  workSheet.setColumnWidth(column + 2, COL_WIDTH * 0.25, GC.Spread.Sheets.SheetArea.viewport);
  workSheet.setColumnWidth(column + 3, COL_WIDTH * 0.25, GC.Spread.Sheets.SheetArea.viewport);
  // -- affects to exog. lines
  workSheet.setColumnWidth(column + 4, COL_WIDTH * 1.618, GC.Spread.Sheets.SheetArea.viewport);

  // -- Row range for columns of Balance Sheet Item Mnemonics
  SheetFunctions.addRange('column', workSheet, 0, column, false);

  // Page Title
  // ===========
  workSheet.setValue(currentRow, 2, result.Mnemonic);
  workSheet.getCell(currentRow, 2).textDecoration(GC.Spread.Sheets.TextDecorationType.underline);
  workSheet.getCell(currentRow, 2).font(getFont('md', true));
  currentRow++;

  // Add the period dates
  numPeriods = result.PeriodDates.length;
  for (let i = 0; i < numPeriods; i++) {
    const currentPeriodColumn = PERIOD_DATES_START_COL + i;
    const cell = workSheet.getCell(currentRow, currentPeriodColumn);

    // PERIOD DATES
    // ------------
    const dateSrcValue = result.PeriodDates[i];
    SheetFunctions.setOutputReportDate(spread, workSheet, cell, new Date(dateSrcValue), false, `[$-${SettingsService.locale}]mmm-yy;@`);

    // Fix 3777 for Detail View: Same column width for the periods
    workSheet.setColumnWidth(currentPeriodColumn, COL_WIDTH + 50, GC.Spread.Sheets.SheetArea.viewport);
  }

  currentRow += 2;

  // Start adding the accounts
  if (isSummaryView) column = 0;

  hierarchyMapping = {};
  const resultset = result;
  // Display Order first, then Account number, then Mnemonic alphabetically
  for (const value of Object.values(result.Results).sortBalanceTree('DisplayOrder', 'Account_Number', 'Mnemonic')) {
    recursiveBuilderForAccounts(workSheet, column, value, result.IgnoreErrors, resultset);
  }
  buildLiquidities(workSheet, result, result.Liquidities, result.IgnoreErrors, result.Survival_Horizon, column);
  buildDetailKRIs({
    sheet: workSheet,
    result,
    ignoreErrors: result.IgnoreErrors,
    startCol: column + 2,
    colShift: 1,
  });

  // Delete unused column
  workSheet.deleteColumns(3, 1);

  // Freeze columns and rows
  workSheet.frozenRowCount(3);
  workSheet.frozenColumnCount(6);

  // Delete extra unused rows
  currentRow += 5;
  workSheet.deleteRows(currentRow - 1, workSheet.getRowCount() - currentRow);
}

function recursivelyBuildAccountHierarchyLevels(result): void {
  // Example of hierarchy levels:
  // ============================
  // - Liquidity Total Liabilities and Equity (-1) (Hierarchy Level 0)
  // --- Liquidity Total Liabilities (2100000) (Hierarchy Level 1)
  // ---- Liquidity Total Deposits (2105000) (Hierarchy Level 2)
  // ----- Liquidity Total Non Maturing Accounts (2110000) (Hierarchy Level 3)
  // ------ Other Internal Accounts (2210000) (Hierarchy Level 4)

  const nodeKey = `${result[hierarchyKey]}`.trim();

  const isRoot = Number(result.Parent_Account_Number) === -1;

  // Recursion stop condition
  if (Object.keys(result.ChildAccounts).length === 0) return;

  // Is root
  if (!hierarchyMapping[nodeKey] && isRoot) hierarchyMapping[nodeKey] = 0;

  // Assign levels to his children tree
  for (const childAccountKey of Object.keys(result.ChildAccounts)) {
    const childAccount = result.ChildAccounts[childAccountKey];
    const childKey = `${childAccount[hierarchyKey]}`.trim();

    if (hierarchyMapping[childKey]) hierarchyMapping[childKey]++;
    else hierarchyMapping[childKey] = 1;

    recursivelyBuildAccountHierarchyLevels(childAccount);
  }
}

function getAccountHierarchyLevel(result) {
  const key = result[hierarchyKey];
  const level = hierarchyMapping[key];
  return !level || isNaN(Number(level)) ? 0 : Number(level);
}

function recursiveBuilderForAccounts(
  workSheet: GC.Spread.Sheets.Worksheet,
  column: number,
  result: any,
  IgnoreErrors: boolean,
  resultset: ResultsetItem
) {
  buildAccountResults(workSheet, column, result, IgnoreErrors, resultset);
  // Display Order first, then Account number, then Mnemonic alphabetically
  for (const value of Object.values(result.ChildAccounts).sortBalanceTree('DisplayOrder', 'Account_Number', 'Mnemonic')) {
    recursiveBuilderForAccounts(workSheet, column, value, IgnoreErrors, resultset);
  }
}

function buildAccountResults(
  workSheet: GC.Spread.Sheets.Worksheet,
  column: number,
  result: any,
  IgnoreErrors: boolean,
  resultset: ResultsetItem
) {
  const inputPropColumn = column + 4;
  const outputPropertyMnemonicColumn = column + 3;
  const rowFontSize = 'sm';
  const hasInputProperties = Object.keys(result.InputProperties).length > 0;
  const groupings = { lvl2Start: -1, lvl3Start: -1, lvl2End: -1, lvl3End: -1, fundingImpactStart: -1, fundingImpactEnd: -1 }; // Initial state
  let groupingCount = 0;
  const isSummaryView = workSheet.name().includes('-summary');

  // ACCOUNT NAME
  // -----------
  recursivelyBuildAccountHierarchyLevels(result);
  const indentationCharacter = `  `;
  const accountName = indentationCharacter.repeat(getAccountHierarchyLevel(result)) + result.Name;

  workSheet.setValue(currentRow, column, accountName);
  workSheet.getCell(currentRow, column).font(getFont(rowFontSize, false));

  currentRow++;

  groupings.lvl2Start = currentRow;
  groupings.lvl3Start = currentRow;

  let k = 0;
  // Start of InputProperties / Input properties / ALM Values
  if (hasInputProperties) {
    for (const key of Object.keys(result.InputProperties)) {
      k = 0;
      // InputProperties (NAME)
      const mnemonic = result.InputProperties[key].Mnemonic;

      if (!isSummaryView) {
        const balanceSheetItemMnemonic = result.Mnemonic || null;
        workSheet.getCell(currentRow, 0).value(balanceSheetItemMnemonic);
        workSheet.getCell(currentRow, 1).value(mnemonic);
      }

      workSheet.setValue(currentRow, inputPropColumn, mnemonic);
      workSheet.getCell(currentRow, inputPropColumn).font(getFont(rowFontSize, false, true));

      for (const per of Object.keys(result.InputProperties[key].BalanceData)) {
        if (result.InputProperties[key].BalanceData[per].HasError && !IgnoreErrors) {
          const error = new ErrorsView();
          error.column = inputPropColumn + 1 + k;
          error.row = currentRow;
          error.message = new MessageGenerator().buildMessage(
            0,
            result.InputProperties[key].BalanceData[per].ErrorCodes[0],
            result.Mnemonic,
            result.InputProperties[key].Mnemonic,
            k,
            ''
          );
          SheetFunctions.addComment(workSheet, currentRow, inputPropColumn + 1 + k, error.message);
          if (
            !errorAlreadyLogged(
              error,
              result,
              result.InputProperties[key].Mnemonic,
              result.Mnemonic,
              k + 1,
              result.InputProperties[key].BalanceData[per].ErrorCodes[0],
              result.InputProperties[key].Mnemonic,
              1
            )
          ) {
            logError(
              error,
              result.InputProperties[key].Mnemonic,
              result.Mnemonic,
              k + 1,
              result.InputProperties[key].BalanceData[per].ErrorCodes[0],
              result.InputProperties[key].Mnemonic,
              1,
              result.ResultSetID,
              result.Mnemonic
            );
          }
        }

        // InputProperties (VALUES) - Values of the input properties
        const val = result.InputProperties[key].BalanceData[per].Value;
        const cell = workSheet.getCell(currentRow, inputPropColumn + 1 + k);
        const customFormatter = null; // Previous custom formatter was '#,##0;0.00';
        SheetFunctions.setOutputReportNumberFormatting(spread, workSheet, currentRow, inputPropColumn + 1 + k, val, customFormatter, false);
        resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);

        // Font overwrite
        cell.font(getFont(rowFontSize, false, false));
        SheetFunctions.centerAndWrapCell(spread, workSheet, currentRow, inputPropColumn + 1 + k);
        k++;
      }
      currentRow++;
    }
    // End of InputProperties

    // Being inside of the IF, avoids a blank row before StartBal when there is no InputProperties
    currentRow++;
  }
  groupings.lvl3End = currentRow;

  // Start of StarBal-EndBal properties
  // Properties are a dictionary, so the ordering is not guaranteed. We sort them to make sure EndBal and SourceUse are in last order
  const createSortValueForProperties = (key: string) => {
    let sortValue = 0;
    switch (key) {
      case 'SourceUse':
        sortValue = 2;
        break;
      case 'FundingImpact':
      case 'EndBal':
        sortValue = result.HasFundingImpactApplied ? 3 : 1;
        break;
      default:
        sortValue = 0;
    }
    return { key, sortValue };
  };

  // FundingImpact and PrefundingEndBal should not be displayed if Funding Impact hasn't been applied
  if (!result.HasFundingImpactApplied) {
    delete result.Category.Properties.FundingImpact;
    delete result.Category.Properties.PreFundingEndBal;
  }
  for (const key of sortPropertiesByKey(result.Category.Properties)) {
    k = 0;
    const propertyMnemonic = result.Category.Properties[key].Mnemonic;

    if (!isSummaryView) {
      const balanceSheetItemMnemonic = result.Mnemonic || null;
      workSheet.getCell(currentRow, 0).value(balanceSheetItemMnemonic);
      workSheet.getCell(currentRow, 1).value(propertyMnemonic === 'SourceUse' ? 'NetSU' : propertyMnemonic);
    }

    let val = '';
    const endBalPropertyName = result.HasFundingImpactApplied ? 'PreFundingEndBal' : 'EndBal';
    if (['StartBal', 'SourceUse', 'PreFundingEndBal', 'EndBal'].includes(propertyMnemonic)) {
      workSheet.setValue(currentRow, outputPropertyMnemonicColumn, propertyMnemonic);
      workSheet.getCell(currentRow, outputPropertyMnemonicColumn).font(getFont(rowFontSize, false, true));
      if (propertyMnemonic === 'SourceUse') {
        workSheet.getCell(currentRow, outputPropertyMnemonicColumn).font(getFont(rowFontSize, true));
        for (let i = 0; i < 3; i++) {
          workSheet
            .getCell(currentRow, outputPropertyMnemonicColumn + i, GC.Spread.Sheets.SheetArea.viewport)
            .borderTop(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.medium));
        }
      }
    } else {
      workSheet.setValue(currentRow, outputPropertyMnemonicColumn + 1, propertyMnemonic);
      workSheet.getCell(currentRow, outputPropertyMnemonicColumn + 1).font(getFont('x-sm', false, true));
    }
    const resultColumnAdd = propertyMnemonic === 'EndBal' ? 2 : 3;
    for (const res of Object.keys(result.Category.Properties[key].Results)) {
      val = result.Category.Properties[key].Results[res].Value;

      // Error display
      if (result.Category.Properties[key].Results[res].HasError && !IgnoreErrors) {
        for (let h = 0; h < result.Category.Properties[key].Results[res].ErrorCodes.length; h++) {
          addErrorToReportAccountResults(resultColumnAdd, key, res, h);
        }
      }

      // Values between StartBal and EndBal
      const cell = workSheet.getCell(currentRow, outputPropertyMnemonicColumn + resultColumnAdd + k);
      SheetFunctions.setOutputReportNumberFormatting(
        spread,
        workSheet,
        currentRow,
        outputPropertyMnemonicColumn + resultColumnAdd + k,
        val
        // Uncommenting the next lines will disable rounding
        // ,null
        // ,false
      );
      resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);

      // Font overwrite
      cell.font(getFont(rowFontSize, false, false));

      if (propertyMnemonic === 'SourceUse') {
        // Borders
        workSheet
          .getCell(currentRow, outputPropertyMnemonicColumn + resultColumnAdd + k, GC.Spread.Sheets.SheetArea.viewport)
          .borderTop(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.medium));
        groupings.lvl2End = currentRow;
      }

      if (propertyMnemonic === 'FundingImpact') {
        groupings.fundingImpactStart = currentRow;
        groupings.fundingImpactEnd = currentRow + 2;
      }

      k++;
    }

    currentRow++;
    if (propertyMnemonic === endBalPropertyName) {
      currentRow++;
    }
  }

  // Range groupings
  // ===============
  // -- Level 2 Grouping
  addGrouping(groupings.lvl2Start, groupings.lvl2End);
  // -- Level 3 Grouping
  addGrouping(groupings.lvl3Start, groupings.lvl3End);
  // additional grouping for funding impact
  if (groupings.fundingImpactStart > 0) {
    addGrouping(groupings.fundingImpactStart, groupings.fundingImpactEnd);
    groupings.fundingImpactStart = -1;
    groupings.fundingImpactEnd = -1;
  }

  currentRow++;

  function addGrouping(start: number, end: number) {
    groupingCount = end - start;
    if (end > -1 && groupingCount > 0) {
      SheetFunctions.addRowRange(workSheet, start, groupingCount);
    }
  }

  function sortPropertiesByKey(properties) {
    return Object.keys(properties)
      .map(createSortValueForProperties)
      .sort((a, b) => a.sortValue - b.sortValue)
      .map(x => x.key);
  }

  function addErrorToReportAccountResults(resultColumnAdd: number, key: string, res: string, h: number) {
    const error = new ErrorsView();
    error.column = outputPropertyMnemonicColumn + resultColumnAdd + k;
    error.row = currentRow;
    let counter = 0;
    if (result.Category.Properties[key].Results[res].Mnemonics && result.Category.Properties[key].Results[res].Mnemonics.length > 0) {
      counter = 1;
      error.message = new MessageGenerator().buildMessage(
        counter,
        result.Category.Properties[key].Results[res].ErrorCodes[h],
        result.Mnemonic,
        result.Category.Properties[key].Mnemonic,
        k + 1,
        result.Category.Properties[key].Results[res].Mnemonics[h]
      );
    } else {
      error.message = new MessageGenerator().buildMessage(
        counter,
        result.Category.Properties[key].Results[res].ErrorCodes[h],
        result.Mnemonic,
        result.Category.Properties[key].Mnemonic,
        k + 1,
        ''
      );
    }

    SheetFunctions.addComment(workSheet, error.row, error.column, error.message);
    const mnem = counter === 0 ? '' : result.Category.Properties[key].Results[res].Mnemonics[0];
    if (
      !errorAlreadyLogged(
        error,
        resultset,
        result.Category.Properties[key],
        result.Mnemonic,
        k + 1,
        result.Category.Properties[key].Results[res].ErrorCodes[h],
        mnem,
        1
      )
    ) {
      logError(
        error,

        result.Category.Properties[key],
        result.Mnemonic,
        k + 1,
        result.Category.Properties[key].Results[res].ErrorCodes[h],
        mnem,
        1,
        resultset.ResultSetID,
        resultset.Mnemonic
      );
    }

    workSheet.getCell(currentRow, outputPropertyMnemonicColumn + resultColumnAdd + k).backColor(FAIL_COLOR);
    result.HasError = true;
  }
}

function buildLiquidities(
  workSheet: GC.Spread.Sheets.Worksheet,
  result,
  liq: any[],
  IgnoreErrors: boolean,
  Survival_Horizon: number,
  column: number
) {
  // Contingency funding section
  // ---------------------------
  workSheet.setValue(currentRow, column, 'Counterbalancing Capacity');
  workSheet.getCell(currentRow, column).font(getFont('sm', true));
  currentRow++;

  // PERIODS
  // =======
  // If you change this starting column, you should change the column for !isSummary inside of addLiquidityToSheet

  // Liquidity Sources Types
  // -- Primary, Secondary, Tertiary...
  const acceptedTypes = SPREAD_CONFIG_LIQSRC.SpreadConfig.LIQ_SOURCES_ACCEPTED_TYPE_VALUES;
  acceptedTypes.forEach((typeName, typeNum) => {
    const newRow = addLiquidityToSheet(
      currentRow,
      spread,
      workSheet,
      liq,
      typeNum + 1,
      IgnoreErrors,
      Survival_Horizon,
      resultsStore,
      result.ResultSetID,
      result.Mnemonic
    );
    currentRow = newRow;
  });
}

function buildDetailKRIs(params: any) {
  const { sheet, result, ignoreErrors, colShift, startCol } = params;
  // Building KRIs for the Detail view...
  const enableDISFilter = false;

  const newRow = addExogenousByGroupID(
    currentRow,
    spread,
    sheet,
    result.Exogenous,
    ignoreErrors,
    startCol,
    colShift,
    enableDISFilter,
    false,
    resultsStore,
    result.ResultSetID,
    result.Mnemonic
  );
  currentRow = newRow;
}
